import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { OverviewStatsTable } from "@/components/general/Dashboard/Overview/OverviewStatsTable";
import { CompactStatsTable } from "@/components/general/Dashboard/Overview/CompactStatsTable";
import {
    PlayerStatsMobileCards,
    GoalkeeperStatsMobileCards,
    StatsMobileCards,
    type StatRow,
} from "@/components/general/Dashboard/Overview/AnalyticsMobileCards";
import { overviewStatsTableColumns } from "@/components/general/Dashboard/Overview/OverviewStatsTableColumns";
import { goalkeeperStatsTableColumns } from "@/components/general/Dashboard/Overview/GoalkeeperStatsTableColumns";
import {
    periodTableColumns,
    periodCombinedTableColumns,
    type PeriodTableRow,
    type PeriodTableRowWithTeam,
} from "@/components/general/Dashboard/Overview/PeriodTableColumns";
import { LinePeriodView } from "@/components/general/Dashboard/LinePeriod/LinePeriodView";
import { LinePeriodBreakdownChart } from "@/components/general/Dashboard/LineMatch/LinePeriodBreakdownChart";
import {
    PlayerStats,
    GoalkeeperStats,
    matchKpisQueryOptions,
    goalkeeperKpisQueryOptions,
    periodPlayersDataQueryOptions,
} from "@/api/ranking";
import type { PeriodPlayerData } from "@/api/ranking";
import { periodLinescoresQueryOptions } from "@/api/lineScores";
import { useSelectedMatch } from "@/context/matchContext";
import { useSelectedTeam } from "@/context/teamContext";
import { useViewMode } from "../page";
import { getTeamLogo } from "@/lib/utils";
import { getPeriodLabel } from "@/components/general/Dashboard/PeriodData/periodDataUtils";

const extractPeriodRow = (player: PeriodPlayerData, period: 1 | 2 | 3): PeriodTableRow => {
    const p = period === 1 ? player.period1 : period === 2 ? player.period2 : player.period3;
    return {
        playerId: player.playerId,
        teamId: player.teamId,
        playerName: player.playerName,
        playerLastName: player.playerLastName,
        playerRole: player.role,
        pointsDefensive: p.pointsDefensive ?? 0,
        pointsOffensive: p.pointsOffensive ?? 0,
        pointsTeamPlay: p.pointsTeamPlay ?? 0,
        pointsEffort: p.pointsEffort ?? 0,
        pointsBonus: p.pointsBonus ?? 0,
        totalPoints: p.totalPoints ?? 0,
    };
};

const toPeriodStatRows = (rows: PeriodTableRow[]): StatRow[] =>
    rows.map((p) => ({
        playerId: p.playerId,
        displayName: `${p.playerName ?? ""} ${p.playerLastName ?? ""}`.trim(),
        role: p.playerRole,
        subScores: [
            { label: "Def", value: p.pointsDefensive },
            { label: "Off", value: p.pointsOffensive },
            { label: "TP", value: p.pointsTeamPlay },
            { label: "Eff", value: p.pointsEffort },
            { label: "Bon", value: p.pointsBonus },
        ],
        totalPoints: p.totalPoints,
    }));

const toPeriodCombinedStatRows = (rows: PeriodTableRowWithTeam[]): StatRow[] =>
    rows.map((p) => ({
        playerId: p.playerId,
        displayName: `${p.playerName ?? ""} ${p.playerLastName ?? ""}`.trim(),
        role: p.playerRole,
        teamLogoUrl: getTeamLogo(p.teamName) ?? undefined,
        subScores: [
            { label: "Def", value: p.pointsDefensive },
            { label: "Off", value: p.pointsOffensive },
            { label: "TP", value: p.pointsTeamPlay },
            { label: "Eff", value: p.pointsEffort },
            { label: "Bon", value: p.pointsBonus },
        ],
        totalPoints: p.totalPoints,
    }));

export const AnalyticsPage = () => {
    const { matchIds, setTournamentName } = useSelectedMatch();
    const { teamId, teamName } = useSelectedTeam();
    const { viewMode } = useViewMode();

    const { data: kpiData, isLoading } = useQuery(
        matchKpisQueryOptions(matchIds.length > 0 ? matchIds : undefined, teamId)
    );
    const { data: goalkeeperData, isLoading: goalkeeperLoading } = useQuery(
        goalkeeperKpisQueryOptions(matchIds.length > 0 ? matchIds : undefined, teamId)
    );
    const singleMatchSelected = matchIds.length === 0 || matchIds.length === 1;
    const { data: periodData, isLoading: periodLoading } = useQuery({
        ...periodPlayersDataQueryOptions(matchIds.length > 0 ? matchIds : undefined, teamId),
        enabled: singleMatchSelected,
    });
    const { data: linePeriodData, isLoading: linePeriodLoading } = useQuery(
        periodLinescoresQueryOptions(matchIds, teamId)
    );

    const [lineSubMode, setLineSubMode] = useState<"all" | number>("all");

    const linePeriods = linePeriodData?.linePeriodData ?? [];
    const currentLinePeriod = linePeriods.find((p) => p.periodId === lineSubMode);

    useEffect(() => {
        if (typeof lineSubMode === "number" && !linePeriods.some((p) => p.periodId === lineSubMode)) {
            setLineSubMode("all");
        }
    }, [linePeriods, lineSubMode]);

    const periodTableData = useMemo(() => {
        const empty = { team: [] as PeriodTableRow[], opponent: [] as PeriodTableRow[] };
        if (!periodData) return { p1: empty, p2: empty, p3: empty };
        const build = (period: 1 | 2 | 3) => ({
            team: periodData.periodKpiData.map((p) => extractPeriodRow(p, period)),
            opponent: periodData.opponentPeriodKpiData.map((p) => extractPeriodRow(p, period)),
        });
        return { p1: build(1), p2: build(2), p3: build(3) };
    }, [periodData]);

    const periodTeamNames = useMemo(
        () => ({
            team: periodData?.periodKpiData[0]?.teamName ?? "Your Team",
            opponent: periodData?.opponentPeriodKpiData[0]?.teamName ?? "Opponent",
        }),
        [periodData]
    );

    const periodCombinedTableData = useMemo(() => {
        const empty: PeriodTableRowWithTeam[] = [];
        if (!periodData) return { p1: empty, p2: empty, p3: empty };
        const build = (period: 1 | 2 | 3): PeriodTableRowWithTeam[] => [
            ...periodData.periodKpiData.map((p) => ({
                ...extractPeriodRow(p, period),
                teamName: periodTeamNames.team,
            })),
            ...periodData.opponentPeriodKpiData.map((p) => ({
                ...extractPeriodRow(p, period),
                teamName: periodTeamNames.opponent,
            })),
        ];
        return { p1: build(1), p2: build(2), p3: build(3) };
    }, [periodData, periodTeamNames]);

    const [periodLayout, setPeriodLayout] = useState<"split" | "combined">("split");
    const periodSectionRef = useRef<HTMLDivElement>(null);
    const [periodSectionWidth, setPeriodSectionWidth] = useState<number>(Infinity);

    useEffect(() => {
        if (viewMode !== "period") return;
        const el = periodSectionRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setPeriodSectionWidth(entry.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [viewMode]);

    const displayData = useMemo(() => {
        if (viewMode === "line" || viewMode === "period") return [];
        return viewMode === "average" ? kpiData?.average : kpiData?.total;
    }, [viewMode, kpiData]);

    const linePeriodTeamNames = useMemo(() => {
        const matchInfo = kpiData?.matches?.[0];
        return {
            chosen: teamName ?? "Your Team",
            opponent: matchInfo
                ? matchInfo.homeTeamId === teamId
                    ? matchInfo.awayTeam
                    : matchInfo.homeTeam
                : "Opponent",
        };
    }, [kpiData, teamId, teamName]);

    const displayGoalkeeperData = viewMode === "average" ? goalkeeperData?.average : goalkeeperData?.total;

    const goalkeeperColumns = useMemo<ColumnDef<GoalkeeperStats>[]>(() => {
        return goalkeeperStatsTableColumns.map((col) => {
            if (col.id === "player") {
                return { ...col, size: 250 };
            }
            return col;
        });
    }, []);

    const playerColumns = useMemo<ColumnDef<PlayerStats>[]>(() => {
        return overviewStatsTableColumns.map((col) => {
            if (col.id === "player") {
                return { ...col, size: 293 };
            }
            return col;
        });
    }, []);

    useEffect(() => {
        if (kpiData?.matches && kpiData.matches.length > 0) {
            setTournamentName(kpiData.matches[0].tournamentName);
        }
    }, [kpiData, setTournamentName]);

    const allDataLoaded = !isLoading && !goalkeeperLoading;

    return (
        <div
            id="onboarding-analytics-data-area"
            className="flex-1 min-h-0 h-full flex flex-col gap-6 overflow-auto p-2"
            data-analytics-loaded={allDataLoaded ? "true" : undefined}
        >
            {viewMode === "line" ? (
                <div className="flex flex-col gap-4">
                    <nav className="rounded-md p-1 bg-surface-container-low inline-flex items-center gap-1">
                        {linePeriods.map((p) => (
                            <button
                                key={p.periodId}
                                type="button"
                                onClick={() => setLineSubMode(p.periodId)}
                                className={`px-3 py-1.5 rounded text-xs font-black uppercase tracking-widest transition-colors cursor-pointer ${
                                    lineSubMode === p.periodId
                                        ? "bg-[#050066] text-white"
                                        : "text-on-surface-variant hover:bg-surface-container"
                                }`}
                            >
                                {getPeriodLabel(p.periodId)}
                            </button>
                        ))}
                        <div className="w-px h-4 bg-outline-variant/30 mx-1" />
                        <button
                            type="button"
                            onClick={() => setLineSubMode("all")}
                            className={`px-3 py-1.5 rounded text-xs font-black uppercase tracking-widest transition-colors cursor-pointer ${
                                lineSubMode === "all"
                                    ? "bg-[#050066] text-white"
                                    : "text-on-surface-variant hover:bg-surface-container"
                            }`}
                        >
                            All
                        </button>
                    </nav>
                    {linePeriodLoading ? (
                        <div className="flex items-center justify-center h-48">
                            <span className="text-sm text-on-surface-variant">Loading line period data...</span>
                        </div>
                    ) : (
                        <>
                            <LinePeriodView
                                period={
                                    lineSubMode === "all"
                                        ? (linePeriodData?.overallLineData ?? undefined)
                                        : currentLinePeriod
                                }
                                teamNames={linePeriodTeamNames}
                            />
                            {lineSubMode === "all" && singleMatchSelected && (
                                <div className="hidden md:block h-[400px]">
                                    <div className="rounded-md border border-outline-variant/20 bg-surface-container-low p-6 h-full flex flex-col">
                                        <div className="mb-2 flex-shrink-0">
                                            <h3 className="text-lg font-headline font-bold text-on-surface">
                                                Line Performance by Period
                                                <span className="text-on-surface-variant"> • </span>
                                                <span className="text-blue-500">Total Points per Line</span>
                                            </h3>
                                        </div>
                                        <div className="flex-1 min-h-0">
                                            <LinePeriodBreakdownChart
                                                data={linePeriodData?.linePeriodData}
                                                overallLineData={linePeriodData?.overallLineData}
                                                teamNames={linePeriodTeamNames}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : viewMode === "period" ? (
                <div ref={periodSectionRef} className="flex flex-col gap-8">
                    {([1, 2, 3] as const).map((period) => {
                        const pKey = `p${period}` as "p1" | "p2" | "p3";
                        const splitData = periodTableData[pKey];
                        const combinedData = periodCombinedTableData[pKey];
                        const gridClass = periodSectionWidth < 1160 ? "grid-cols-1" : "grid-cols-2";
                        return (
                            <div key={period} className="flex flex-col gap-3">
                                <div className="flex items-center justify-between px-1">
                                    <h3 className="text-lg font-headline font-bold section-title whitespace-nowrap">
                                        Period {period}
                                    </h3>
                                    <div className="flex rounded-md p-0.5 gap-0.5 data-table-wrapper">
                                        {(["split", "combined"] as const).map((layout) => (
                                            <button
                                                key={layout}
                                                onClick={() => setPeriodLayout(layout)}
                                                className={`px-3 py-1 text-[10px] font-headline font-black uppercase tracking-widest rounded transition-all duration-150 cursor-pointer ${
                                                    periodLayout === layout
                                                        ? "bg-white text-primary shadow-sm"
                                                        : "text-on-surface-variant hover:text-on-surface"
                                                }`}
                                            >
                                                {layout === "split" ? "Split" : "Combined"}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {periodLayout === "combined" ? (
                                    <>
                                        <div className="md:hidden">
                                            <StatsMobileCards
                                                data={toPeriodCombinedStatRows(combinedData)}
                                                isLoading={periodLoading}
                                            />
                                        </div>
                                        <div className="hidden md:block rounded-md overflow-hidden border border-outline-variant/20">
                                            <CompactStatsTable<PeriodTableRowWithTeam>
                                                columns={periodCombinedTableColumns}
                                                data={combinedData}
                                                isLoading={periodLoading}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className={`grid ${gridClass} gap-4`}>
                                        <div className="flex flex-col gap-1.5">
                                            <h4 className="text-sm font-semibold text-on-surface px-1 flex items-center gap-1.5">
                                                {(() => {
                                                    const logo = getTeamLogo(periodTeamNames.team);
                                                    return logo ? (
                                                        <img
                                                            src={logo}
                                                            alt={periodTeamNames.team}
                                                            className="h-4 w-auto object-contain"
                                                        />
                                                    ) : null;
                                                })()}
                                                {periodTeamNames.team}
                                            </h4>
                                            <div className="md:hidden">
                                                <StatsMobileCards
                                                    data={toPeriodStatRows(splitData.team)}
                                                    isLoading={periodLoading}
                                                />
                                            </div>
                                            <div className="hidden md:block rounded-md overflow-hidden border border-outline-variant/20">
                                                <CompactStatsTable<PeriodTableRow>
                                                    columns={periodTableColumns}
                                                    data={splitData.team}
                                                    isLoading={periodLoading}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <h4 className="text-sm font-semibold text-on-surface px-1 flex items-center gap-1.5">
                                                {(() => {
                                                    const logo = getTeamLogo(periodTeamNames.opponent);
                                                    return logo ? (
                                                        <img
                                                            src={logo}
                                                            alt={periodTeamNames.opponent}
                                                            className="h-4 w-auto object-contain"
                                                        />
                                                    ) : null;
                                                })()}
                                                {periodTeamNames.opponent}
                                            </h4>
                                            <div className="md:hidden">
                                                <StatsMobileCards
                                                    data={toPeriodStatRows(splitData.opponent)}
                                                    isLoading={periodLoading}
                                                />
                                            </div>
                                            <div className="hidden md:block rounded-md overflow-hidden border border-outline-variant/20">
                                                <CompactStatsTable<PeriodTableRow>
                                                    columns={periodTableColumns}
                                                    data={splitData.opponent}
                                                    isLoading={periodLoading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <>
                    <div className="flex flex-col gap-3">
                        <h3 className="text-lg font-headline font-bold section-title">Goalkeeper Statistics</h3>
                        <div
                            id="onboarding-analytics-goalkeeper-table"
                            className="rounded-md border-table-border-color"
                        >
                            <div className="lg:hidden">
                                <GoalkeeperStatsMobileCards
                                    data={displayGoalkeeperData || []}
                                    isLoading={goalkeeperLoading}
                                />
                            </div>
                            <div className="hidden lg:block">
                                <OverviewStatsTable<GoalkeeperStats>
                                    columns={goalkeeperColumns}
                                    data={displayGoalkeeperData || []}
                                    isLoading={goalkeeperLoading}
                                    showTier={viewMode === "average"}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <h3 className="text-lg font-headline font-bold section-title">Player Statistics</h3>
                        <div id="onboarding-analytics-player-table" className="rounded-md border-table-border-color">
                            <div className="lg:hidden">
                                <PlayerStatsMobileCards data={displayData || []} isLoading={isLoading} />
                            </div>
                            <div className="hidden lg:block">
                                <OverviewStatsTable<PlayerStats>
                                    columns={playerColumns}
                                    data={displayData || []}
                                    isLoading={isLoading}
                                    showTier={viewMode === "average"}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
